import React, { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { Download, Copy, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { themeVar, useTheme } from "@/lib/theme";

// Mermaid caches its theme at initialize() time, so a theme switch has to
// re-initialize it. Diagrams are always drawn on a SOLID near-black canvas
// (never glass) so exported SVG/PNG stays readable outside the app.
let initializedFor = null;
function ensureMermaidInit(themeId) {
    if (initializedFor === themeId) return;
    const accent = themeVar("--neon-blue", "var(--neon-cyan)");
    const accentBright = themeVar("--neon-cyan", "var(--neon-cyan)");
    mermaid.initialize({
        startOnLoad: false,
        theme: "dark",
        themeVariables: {
            darkMode: true,
            background: "#08080C",
            primaryColor: "#18181B",
            primaryTextColor: "#E2E8F0",
            primaryBorderColor: accent,
            lineColor: "#8A8F9C",
            fontFamily: "JetBrains Mono, monospace",
            // Keep edge/relationship labels on the dark canvas instead of
            // Mermaid's default light-grey chip, which reads as a visual bug
            // on the app's near-black diagram surface.
            edgeLabelBackground: "#08080C",
            labelBackground: "#08080C",
            tertiaryColor: "#08080C",
            secondaryColor: "#18181B",
            clusterBkg: "#08080C",
            clusterBorder: accentBright,
            nodeTextColor: "#E4E4E7",
            textColor: "#D4D4D8",
        },
        // Pure-SVG <text> labels instead of <foreignObject><div> HTML labels —
        // this is also what makes PNG canvas export work (foreignObject HTML
        // content taints the canvas in most browsers, blocking toBlob()).
        flowchart: { htmlLabels: false },
        securityLevel: "strict",
    });
    initializedFor = themeId;
}

let renderCounter = 0;

/** Renders a Mermaid diagram definition string as an SVG, with Copy Code /
 * Download SVG / Download PNG actions. Used by the Sentinel Forge "Diagrams"
 * tab (Diagram Studio). */
export function MermaidRenderer({ code, title }) {
    const containerRef = useRef(null);
    const [svg, setSvg] = useState("");
    const [error, setError] = useState("");
    const [rendering, setRendering] = useState(false);
    const { theme } = useTheme();

    useEffect(() => {
        if (!code) return;
        ensureMermaidInit(theme);
        let cancelled = false;
        setRendering(true);
        setError("");
        renderCounter += 1;
        const id = `mermaid-diagram-${renderCounter}`;
        mermaid.render(id, code)
            .then(({ svg: out }) => { if (!cancelled) setSvg(out); })
            .catch((e) => { if (!cancelled) setError(e?.message || "Invalid diagram syntax"); })
            .finally(() => { if (!cancelled) setRendering(false); });
        return () => { cancelled = true; };
    }, [code, theme]);

    const safeName = () => (title || "diagram").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 60);

    const downloadSvg = () => {
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${safeName()}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const downloadPng = () => {
        const svgEl = containerRef.current?.querySelector("svg");
        if (!svgEl) return;
        const width = svgEl.viewBox?.baseVal?.width || svgEl.width?.baseVal?.value || 1200;
        const height = svgEl.viewBox?.baseVal?.height || svgEl.height?.baseVal?.value || 800;
        const svgData = new XMLSerializer().serializeToString(svgEl);
        // A base64 data: URI (not a blob: URL) avoids Chrome's canvas-taint
        // quirk when drawing an SVG <img> onto a <canvas> for export.
        const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
        const img = new Image();
        img.onload = () => {
            const scale = 2;
            const canvas = document.createElement("canvas");
            canvas.width = width * scale;
            canvas.height = height * scale;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#05050A";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (!blob) { toast.error("PNG export failed"); return; }
                const pngUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = pngUrl;
                a.download = `${safeName()}.png`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(pngUrl);
            }, "image/png");
        };
        img.onerror = () => toast.error("PNG export failed \u2014 could not rasterize diagram");
        img.src = dataUri;
    };

    const copyCode = () => {
        if (!navigator.clipboard?.writeText) {
            toast.error("Copy failed \u2014 clipboard unavailable in this browser");
            return;
        }
        navigator.clipboard.writeText(code || "")
            .then(() => toast.success("Mermaid code copied to clipboard"))
            .catch(() => toast.error("Copy failed \u2014 clipboard blocked"));
    };

    if (!code) return null;

    return (
        <div data-testid={TID.diagramRenderContainer} className="panel-elevated p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs font-mono text-cyan-300 truncate">{title}</div>
                <div className="flex items-center gap-3">
                    <button data-testid={TID.diagramCopyCodeBtn} onClick={copyCode} className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-400 hover:text-cyan-300 transition-colors" title="Copy Mermaid code">
                        <Copy size={12} /> Code
                    </button>
                    <button data-testid={TID.diagramDownloadSvgBtn} onClick={downloadSvg} disabled={!svg} className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-400 hover:text-cyan-300 transition-colors disabled:opacity-40" title="Download SVG">
                        <Download size={12} /> SVG
                    </button>
                    <button data-testid={TID.diagramDownloadPngBtn} onClick={downloadPng} disabled={!svg} className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-400 hover:text-cyan-300 transition-colors disabled:opacity-40" title="Download PNG">
                        <Download size={12} /> PNG
                    </button>
                </div>
            </div>
            <div ref={containerRef} className="bg-[var(--bg-base)] rounded-sm border border-white/10 p-4 overflow-auto max-h-[520px]">
                {rendering && (
                    <div className="flex items-center justify-center gap-2 text-slate-500 text-xs font-mono py-10">
                        <Loader2 size={14} className="animate-spin" /> Rendering diagram…
                    </div>
                )}
                {error && !rendering && (
                    <div className="flex items-center gap-2 text-rose-300 text-xs font-mono py-6">
                        <AlertTriangle size={14} /> {error}
                    </div>
                )}
                {!rendering && !error && <div dangerouslySetInnerHTML={{ __html: svg }} />}
            </div>
        </div>
    );
}
